import {
  createContext,
  useContext,
  useEffect,
  useState,
  type FC,
  type ReactNode,
} from 'react';
import { loadStripe, type Stripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';

// ============================================================================
// Types
// ============================================================================

interface StripeContextValue {
  /**
   * Promise that resolves to the Stripe instance.
   * Use this when you need to pass Stripe to the Elements provider.
   */
  stripePromise: Promise<Stripe | null> | null;

  /**
   * The resolved Stripe instance (null while loading).
   * Use this for direct Stripe API calls.
   */
  stripe: Stripe | null;

  /**
   * The Stripe publishable key from the backend config.
   */
  publishableKey: string | null;

  /**
   * The default currency for payments (e.g., 'gbp').
   */
  currency: string;

  /**
   * Whether Stripe is in test mode (using test keys).
   */
  isTestMode: boolean;

  /**
   * Whether the Stripe configuration is still loading.
   */
  isLoading: boolean;

  /**
   * Error message if Stripe initialization failed.
   */
  error: string | null;
}

interface StripeProviderProps {
  children: ReactNode;
}

interface StripeElementsWrapperProps {
  children: ReactNode;
  /**
   * Optional client secret for Payment Intents.
   * When provided, configures Elements for a specific payment.
   */
  clientSecret?: string;
}

// ============================================================================
// Context
// ============================================================================

const StripeContext = createContext<StripeContextValue | undefined>(undefined);

// ============================================================================
// Hook
// ============================================================================

/**
 * Hook to access the Stripe context.
 * Must be used within a StripeProvider.
 *
 * @example
 * ```tsx
 * function PaymentForm() {
 *   const { stripe, isLoading, error, currency } = useStripeContext();
 *
 *   if (isLoading) return <div>Loading...</div>;
 *   if (error) return <div>Error: {error}</div>;
 *
 *   return <div>Ready for payments in {currency.toUpperCase()}</div>;
 * }
 * ```
 */
export function useStripeContext(): StripeContextValue {
  const context = useContext(StripeContext);
  if (!context) {
    throw new Error('useStripeContext must be used within StripeProvider');
  }
  return context;
}

/**
 * Alias for useStripeContext for backwards compatibility.
 * @deprecated Use useStripeContext instead.
 */
export const useStripe = useStripeContext;

// ============================================================================
// Provider Component
// ============================================================================

/**
 * StripeProvider initializes Stripe by fetching configuration from the backend
 * and loading the Stripe.js library.
 *
 * Wrap your application (or payment-related subtree) with this provider.
 *
 * @example
 * ```tsx
 * function App() {
 *   return (
 *     <StripeProvider>
 *       <Router>
 *         <Routes />
 *       </Router>
 *     </StripeProvider>
 *   );
 * }
 * ```
 */
export const StripeProvider: FC<StripeProviderProps> = ({ children }) => {
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);
  const [stripe, setStripe] = useState<Stripe | null>(null);
  const [publishableKey, setPublishableKey] = useState<string | null>(null);
  const [currency, setCurrency] = useState<string>('gbp');
  const [isTestMode, setIsTestMode] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const initializeStripe = async () => {
      try {
        const apiUrl = import.meta.env.VITE_API_URL || '';
        const response = await fetch(`${apiUrl}/api/payments/config`, {
          credentials: 'include',
        });

        if (!response.ok) {
          throw new Error('Failed to fetch Stripe configuration');
        }

        const result = await response.json();

        if (result.success && result.data) {
          const {
            publishableKey: pk,
            currency: curr,
            isTestMode: testMode,
          } = result.data;

          setPublishableKey(pk);
          setCurrency(curr || 'gbp');
          setIsTestMode(testMode ?? pk.startsWith('pk_test_'));

          // Create the stripe promise - this is used by Elements provider
          const promise = loadStripe(pk);
          setStripePromise(promise);

          // Also resolve and store the instance for direct API calls
          const stripeInstance = await promise;
          setStripe(stripeInstance);
        } else {
          throw new Error(result.error || 'Invalid Stripe configuration response');
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to initialize Stripe';
        setError(errorMessage);
        console.error('Stripe initialization error:', err);
      } finally {
        setIsLoading(false);
      }
    };

    initializeStripe();
  }, []);

  const value: StripeContextValue = {
    stripePromise,
    stripe,
    publishableKey,
    currency,
    isTestMode,
    isLoading,
    error,
  };

  return <StripeContext.Provider value={value}>{children}</StripeContext.Provider>;
};

// ============================================================================
// Elements Wrapper Component
// ============================================================================

/**
 * StripeElementsWrapper wraps children in the Stripe Elements provider.
 * Use this when you need to render Stripe Elements (CardElement, PaymentElement, etc.).
 *
 * Handles loading and error states automatically.
 *
 * @example
 * ```tsx
 * function CheckoutPage() {
 *   return (
 *     <StripeElementsWrapper clientSecret={clientSecret}>
 *       <PaymentElement />
 *       <SubmitButton />
 *     </StripeElementsWrapper>
 *   );
 * }
 * ```
 */
export const StripeElementsWrapper: FC<StripeElementsWrapperProps> = ({
  children,
  clientSecret,
}) => {
  const { stripePromise, isLoading, error } = useStripeContext();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8" role="status" aria-live="polite">
        <div className="flex flex-col items-center gap-3">
          <div
            className="h-8 w-8 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600"
            aria-hidden="true"
          />
          <span className="text-sm text-gray-600">Loading payment system...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="rounded-lg border border-red-200 bg-red-50 p-4"
        role="alert"
        aria-live="assertive"
      >
        <div className="flex items-start gap-3">
          <svg
            className="h-5 w-5 flex-shrink-0 text-red-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <div>
            <h3 className="font-medium text-red-800">Payment System Error</h3>
            <p className="mt-1 text-sm text-red-700">{error}</p>
            <p className="mt-2 text-sm text-red-600">
              Please refresh the page or try again later.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!stripePromise) {
    return (
      <div
        className="rounded-lg border border-yellow-200 bg-yellow-50 p-4"
        role="alert"
        aria-live="polite"
      >
        <div className="flex items-start gap-3">
          <svg
            className="h-5 w-5 flex-shrink-0 text-yellow-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <div>
            <h3 className="font-medium text-yellow-800">Payment System Unavailable</h3>
            <p className="mt-1 text-sm text-yellow-700">
              The payment system is not available at the moment.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Build Elements options
  const elementsOptions: {
    clientSecret?: string;
    appearance: {
      theme: 'stripe';
      variables: {
        colorPrimary: string;
        colorBackground: string;
        colorText: string;
        colorDanger: string;
        fontFamily: string;
        borderRadius: string;
        spacingUnit: string;
      };
    };
    locale: 'en';
  } = {
    appearance: {
      theme: 'stripe',
      variables: {
        colorPrimary: '#0891b2', // primary-600
        colorBackground: '#ffffff',
        colorText: '#1f2937', // gray-800
        colorDanger: '#dc2626', // red-600
        fontFamily: 'Inter, system-ui, sans-serif',
        borderRadius: '8px',
        spacingUnit: '4px',
      },
    },
    locale: 'en',
  };

  // Add clientSecret if provided (for Payment Intents)
  if (clientSecret) {
    elementsOptions.clientSecret = clientSecret;
  }

  return (
    <Elements stripe={stripePromise} options={elementsOptions}>
      {children}
    </Elements>
  );
};

// ============================================================================
// Exports
// ============================================================================

export { StripeContext };
export type { StripeContextValue, StripeProviderProps, StripeElementsWrapperProps };
